/**
 * The filter header — a chips row that expands IN PLACE to reveal the
 * filter controls, pushing the dashboard below it down (no overlay).
 *
 * Collapsed: the ``FilterChips`` summary (every filter, incl. "All").
 * Expanded: the shared ``GenericFilterBar`` (the classic embedded banner
 * bar, ``wrap`` layout) slides open under the chips. Because the panel
 * lives in the document flow, the KPIs / charts / tables below simply
 * reflow down — nothing is covered, the nav and the chat keep their
 * edges, and the date panel gets the banner's full width.
 *
 * The open/close is a ``grid-template-rows`` 0fr↔1fr transition, NOT a
 * measured-height animation: the panel content stays MOUNTED and slides
 * up/down under the clip in both directions, so there's no
 * content-vanishes-then-empty-box-shrinks jank and no dependence on a
 * ResizeObserver firing. The collapsed content is kept out of the tab
 * order and pointer flow with ``inert``. ``overflow`` is ``visible``
 * only while open AND settled, so a filter select / date panel dropdown
 * escapes the box at rest; during the slide (and when collapsed) it
 * clips.
 *
 * Filtering is IMMEDIATE (no draft / Apply): with the data visible below
 * while you edit, batching lost its reason — a filter change writes
 * straight through ``onFilterChange`` and the host refetches with its
 * stale-while-revalidate SWR. The chips reflect the change at once.
 *
 * Single copy — both the portal (``GenericDashboardPage``) and the
 * builder Preview (``PreviewModal``) mount THIS, so they render
 * identically. The only seam is the callbacks each host wires in.
 */
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type TransitionEvent,
} from "react";

import {
  MOTION,
  SegmentedTabs,
  usePrefersReducedMotion,
} from "@pervasivemind/bi-components";
import { ChevronDown, MinusCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { FilterChips } from "./FilterChips";
import { countUserOverrides, filterStates, hasUserOverride } from "./filter-state";
import { formatFilterValue } from "./filter-format";
import { resolveFilterLabel } from "./filter-format";
import { filtersForTab } from "./filter-tabs";
import { GenericFilterBar } from "./GenericFilterBar";
import type { FetchRangeHistogram } from "./number-range";
import { useLang } from "./i18n-binding";
import { resolveLocalized } from "./i18n-resolve";
import type { DashboardFilter } from "./types";

export interface CollapsibleFilterHeaderProps {
  filters: ReadonlyArray<DashboardFilter>;
  filterOptions: Record<string, string[]>;
  activeFilters: Record<string, string>;
  /** Immediate write — same semantics as the classic banner bar. */
  onFilterChange: (key: string, value: string) => void;
  /** "Clear all" → back to the report defaults (shared semantics). */
  onClear: () => void;
  /** Chip × — instant single-filter reset. */
  onResetFilter: (filt: DashboardFilter) => void;
  /** DSA "Filtros guardados": forwarded to the inner bar. */
  onSaveView?: () => void;
  /** The SKU list picker's "Upload a file…": forwarded to the inner bar. */
  onUploadSkuList?: () => void;
  /** Range histograms: forwarded to the inner bar. */
  fetchRangeHistogram?: FetchRangeHistogram;
  /** ``new Date()`` — injected so tests can pin relative-date output. */
  today: Date;
  /** The current dashboard tab (resolved id). Filters whose ``tabs``
   *  do not name it are left out of the pinned row, the chips AND the
   *  drawer — they still apply to the query. Undefined/null = every
   *  filter (a dashboard without tabs; every pre-existing host). */
  activeTab?: string | null;
  /** Report groups: the group filters the OPEN report declared «no
   *  aplica», and that report's name. A PINNED one stays in its place,
   *  disabled, with a dashed outline and its value struck through, so
   *  moving between tabs moves nothing (Pablo, 2026-10-25, the rows
   *  rule F2 B). The others (F1 A) leave the chips and the drawer; one
   *  the reader SET stays visible as a struck-through chip "no aplica
   *  en <report>" (a value is never hidden), and the open drawer names
   *  them all in one line at its foot. Their values are kept for the
   *  other reports of the group. Undefined = a plain report. */
  notApplicable?: { keys: ReadonlySet<string>; reportLabel: string } | null;
  /** Report groups: some tab of the group leaves out a pinned filter.
   *  Then the line under the pinned row that names them ("No aplica en
   *  <report>: …") is there on EVERY tab, empty when nothing pinned is
   *  out, so the header keeps its height from tab to tab. */
  reserveNotApplicableLine?: boolean;
  /** ``"dark"`` (default): the navy banner. ``"light"``: the white
   *  ``ControlPanel`` of the nuevo look (iteración 2, 2026-10-24). */
  surface?: "dark" | "light";
}

export function CollapsibleFilterHeader({
  filters,
  filterOptions,
  activeFilters,
  onFilterChange,
  onClear,
  onResetFilter,
  today,
  onSaveView,
  onUploadSkuList,
  fetchRangeHistogram,
  activeTab,
  notApplicable,
  reserveNotApplicableLine = false,
  surface = "dark",
}: CollapsibleFilterHeaderProps): JSX.Element {
  const dark = surface !== "light";
  const [expanded, setExpanded] = useState(false);
  // Phones (below md): the whole header folds behind one summary row,
  // closed by default. At 390 px the pinned axes, the chips and the tabs
  // filled the first screen and the first number came after it
  // (impeccable critique of Disponibilidad, 2026-10-27).
  const [phoneOpen, setPhoneOpen] = useState(false);
  // "Settled" = the open/close transition has finished. overflow only
  // goes visible when open AND settled.
  const [settled, setSettled] = useState(true);
  const reduced = usePrefersReducedMotion();
  const { t } = useTranslation();
  const locale = useLang();

  // CHS 2026-07 — pinned filters render as always-visible segmented
  // controls OUTSIDE the drawer (the report's primary axes deserve
  // banner real estate); the chips row summarizes only the REST, and
  // the drawer swaps each pinned control for a dropdown (`select`)
  // so the same key stays reachable inside without duplicating the
  // tabs. `pinned` is render-only — the engine's WHERE is untouched.
  // Per-tab visibility (DSA, render-only too): every surface below
  // derives from the filters that belong on the current tab.
  const visible = useMemo(() => {
    const onTab = filtersForTab(filters, activeTab);
    if (!notApplicable || notApplicable.keys.size === 0) return onTab;
    return onTab.filter((f) => !notApplicable.keys.has(f.key));
  }, [filters, activeTab, notApplicable]);
  // The «no aplica» filters, in group order: the ones with a value the
  // reader set become struck-through chips; the drawer line names all.
  const notApplicableFilters = useMemo(
    () => (notApplicable ? filters.filter((f) => notApplicable.keys.has(f.key)) : []),
    [filters, notApplicable],
  );
  // A pinned one already shows its value in place (struck through): no
  // chip for it, which would also widen the chips row on this tab only.
  const setNotApplicable = useMemo(() => {
    const unpinned = notApplicableFilters.filter((f) => !f.pinned);
    if (unpinned.length === 0) return [];
    const states = filterStates(unpinned, activeFilters, today);
    return unpinned.filter((_, i) => hasUserOverride(states[i]!));
  }, [notApplicableFilters, activeFilters, today]);
  // Pinned filters keep their place on every tab, «no aplica» included.
  const pinnedFilters = useMemo(
    () => filtersForTab(filters, activeTab).filter((f) => f.pinned),
    [filters, activeTab],
  );
  const pinnedNotApplicable = useMemo(
    () => (notApplicable ? pinnedFilters.filter((f) => notApplicable.keys.has(f.key)) : []),
    [pinnedFilters, notApplicable],
  );
  const unpinnedFilters = useMemo(
    () => visible.filter((f) => !f.pinned),
    [visible],
  );
  const drawerFilters = useMemo(
    () =>
      visible.map((f) =>
        f.pinned && f.type === "tabs" ? { ...f, type: "select" } : f,
      ),
    [visible],
  );
  // The phone summary: the pinned axes' values in words, and how many of
  // the other filters the reader changed. Nothing is hidden for good: the
  // row says what is set and opens the controls.
  const phoneSummary = useMemo(
    () =>
      pinnedFilters
        .map((f) => {
          const raw = f.locked ?? activeFilters[f.key] ?? "";
          if (!raw) return "";
          return resolveLocalized(f.option_labels?.[raw] ?? null, locale) || raw;
        })
        .filter(Boolean)
        .join(" · "),
    [pinnedFilters, activeFilters, locale],
  );
  const phoneChanged = useMemo(
    () => countUserOverrides(filterStates(unpinnedFilters, activeFilters, today)),
    [unpinnedFilters, activeFilters, today],
  );
  const panelId = useId();
  const phonePanelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const clipRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Trigger toggles; a chip click always expands (focusing that filter
  // is a follow-up).
  const toggle = (focusKey?: string) => {
    setExpanded((prev) => (focusKey ? true : !prev));
  };

  // Unsettle while the slide runs; settle on transitionend. The timer is
  // the backstop for reduced-motion, an interrupted slide, or a browser
  // that can't animate grid-template-rows (settles instantly then).
  useEffect(() => {
    if (settleTimerRef.current !== null) clearTimeout(settleTimerRef.current);
    if (reduced) {
      setSettled(true);
      return undefined;
    }
    setSettled(false);
    settleTimerRef.current = setTimeout(
      () => setSettled(true),
      MOTION.durSlow + 80,
    );
    return () => {
      if (settleTimerRef.current !== null) clearTimeout(settleTimerRef.current);
    };
  }, [expanded, reduced]);

  const onTransitionEnd = (e: TransitionEvent<HTMLDivElement>) => {
    if (
      e.target === gridRef.current &&
      e.propertyName === "grid-template-rows"
    ) {
      if (settleTimerRef.current !== null) clearTimeout(settleTimerRef.current);
      setSettled(true);
    }
  };

  // Collapsed content is ``inert`` — clipped to 0 height AND out of the
  // tab order / pointer flow, while staying mounted for the smooth
  // slide. Set as the content ATTRIBUTE (not the ``.inert`` IDL prop) so
  // it reflects for any consumer/test and works across React versions.
  useEffect(() => {
    clipRef.current?.toggleAttribute("inert", !expanded);
  }, [expanded]);

  // Escape collapses. A React `onKeyDown` on THIS subtree (rootRef), not
  // a native listener: the select / autocomplete / multi pickers swallow
  // their own Escape with a React `onKeyDown` + stopPropagation, so with
  // a dropdown open this handler never runs and only the dropdown closes
  // (audit #203 — a native listener on rootRef ran BEFORE React's
  // delegated dispatch and collapsed the panel with the dropdown still
  // open). The date panel swallows its Escape in a document-CAPTURE
  // handler, which also stops it before React sees it. A second Escape
  // collapses the panel. Focus lives inside rootRef whenever the panel
  // is open (first control, else the trigger).
  const onRootKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (!expanded || e.key !== "Escape") return;
    // Synthetic stopPropagation also stops the NATIVE event at the React
    // root, so a host modal's document-level Escape (builder Preview)
    // still never fires.
    e.stopPropagation();
    setExpanded(false);
    // The Filters trigger, not the phone summary row (also aria-expanded,
    // and display:none from md up).
    Array.from(rootRef.current?.querySelectorAll<HTMLElement>("[aria-expanded]") ?? [])
      .find((el) => el.getAttribute("aria-controls") === panelId)
      ?.focus();
  };

  // Focus the first control when the panel opens (the inert flag was
  // cleared by the effect above, which runs first). preventScroll so
  // expanding never jumps the page.
  useEffect(() => {
    if (!expanded) return undefined;
    const raf = requestAnimationFrame(() => {
      panelRef.current
        ?.querySelector<HTMLElement>("button, input, [tabindex]")
        ?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(raf);
  }, [expanded]);

  const clip = !(expanded && settled);

  // «No aplica» note of the pinned axes. With pinned axes it sits at the
  // END of their row, so it takes no height of its own (encabezado
  // compacto H1c, 2026-10-26: the reserved 16px line was most of the gap
  // between the axes and the chips). Without pinned axes it keeps its
  // own reserved line, so the header still keeps its height per tab.
  const pinnedNote = (placement: "inline" | "line"): JSX.Element => (
    <p
      data-pinned-not-applicable={pinnedNotApplicable.map((f) => f.key).join(",")}
      className={`flex h-4 min-w-0 items-center gap-1 text-[11px] ${
        placement === "inline" ? "ml-auto max-w-full" : "-mt-1 mb-2 px-2"
      } ${dark ? "text-white/75" : "text-neutral-500"}`}
    >
      {notApplicable && pinnedNotApplicable.length > 0 && (
        <>
          <MinusCircle size={12} aria-hidden className="flex-none" />
          <span className="truncate">
            {t("filters.pinnedNotApplicable", {
              report: notApplicable.reportLabel,
              filters: pinnedNotApplicable.map((f) => resolveFilterLabel(f, t, locale)).join(", "),
            })}
          </span>
        </>
      )}
    </p>
  );

  return (
    <div ref={rootRef} onKeyDown={onRootKeyDown}>
      <button
        type="button"
        data-phone-filter-summary=""
        aria-expanded={phoneOpen}
        aria-controls={phonePanelId}
        onClick={() => setPhoneOpen((v) => !v)}
        className={`flex w-full items-center gap-2 px-2 py-1 text-left text-xs md:hidden ${
          dark ? "text-white" : "text-neutral-700"
        }`}
      >
        <span className="min-w-0 flex-1 truncate font-semibold">
          {phoneSummary || t("filters.title")}
        </span>
        {phoneChanged > 0 && (
          <span
            className={`flex-none rounded-full px-2 py-0.5 text-2xs font-bold tabular-nums ${
              dark ? "bg-white/15 text-white" : "bg-primary-soft text-primary-ink"
            }`}
          >
            {t("filters.phoneChanged", { count: phoneChanged })}
          </span>
        )}
        <span className={`flex-none font-semibold ${dark ? "text-white" : "text-primary-ink"}`}>
          {phoneOpen ? t("filters.phoneHide") : t("filters.phoneShow")}
        </span>
        <ChevronDown
          size={14}
          aria-hidden
          className={`flex-none transition-transform duration-200 ${phoneOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div id={phonePanelId} className={phoneOpen ? "mt-2 md:mt-0" : "hidden md:block"}>
      {/* Pinned axes — always visible, never fold into the drawer. One
          segmented control per pinned filter, same accent tone as the
          embedded banner tabs so the row reads as part of the banner. */}
      {pinnedFilters.length > 0 && (
        // px-2 mirrors the FilterChips container's inner padding so the
        // pinned labels align with the Filters trigger below (ronda 3).
        <div className="mb-2 flex flex-wrap items-center gap-x-6 gap-y-2 px-2">
          {pinnedFilters.map((filt) => {
            const options = filt.options?.length
              ? filt.options
              : (filterOptions[filt.key] ?? []);
            if (options.length === 0) return null;
            const value = activeFilters[filt.key] ?? "";
            const na = notApplicable?.keys.has(filt.key) ?? false;
            return (
              <div
                key={filt.key}
                data-not-applicable={na ? filt.key : undefined}
                title={na ? t("filters.notApplicableIn", { report: notApplicable?.reportLabel ?? "" }) : undefined}
                // «No aplica»: dashed OUTLINE (it takes no room, the row
                // does not move) and the chosen pill struck through:
                // shape and text, never colour alone.
                className={`flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2${
                  na
                    ? ` rounded-lg outline-dashed outline-1 outline-offset-2 [&_[aria-checked=true]]:line-through ${
                        dark ? "outline-white/40" : "outline-neutral-400"
                      }`
                    : ""
                }`}
              >
                <span
                  className={`shrink-0 text-[11px] font-semibold uppercase tracking-wide ${
                    dark ? "text-white/80" : "text-neutral-500"
                  }`}
                >
                  {resolveFilterLabel(filt, t, locale)}
                </span>
                <SegmentedTabs
                  className="min-w-0"
                  // A locked pinned axis must not emit a value. The value
                  // stays what the SQL sees; ``option_labels`` (bilingual)
                  // is what the pill shows, same as the drawer and chips.
                  options={options.map((opt) => ({
                    value: String(opt),
                    label:
                      resolveLocalized(
                        filt.option_labels?.[String(opt)] ?? null,
                        locale,
                      ) || String(opt),
                    disabled: Boolean(filt.locked) || na,
                  }))}
                  value={filt.locked ?? (value || null)}
                  onChange={(v: string) => onFilterChange(filt.key, v)}
                  shape="pill"
                  fill="inline"
                  size="sm"
                  tone={dark ? "accent" : "navy"}
                  ariaLabel={resolveFilterLabel(filt, t, locale)}
                />
              </div>
            );
          })}
          {reserveNotApplicableLine && pinnedNote("inline")}
        </div>
      )}
      {reserveNotApplicableLine && pinnedFilters.length === 0 && pinnedNote("line")}
      <FilterChips
        filters={unpinnedFilters}
        activeFilters={activeFilters}
        today={today}
        expanded={expanded}
        onToggle={toggle}
        onResetFilter={onResetFilter}
        onClearAll={onClear}
        controlsId={panelId}
        embedded
        surface={surface}
        filterOptions={filterOptions}
        onFilterChange={onFilterChange}
        notApplicable={
          notApplicable && setNotApplicable.length > 0
            ? { filters: setNotApplicable, reportLabel: notApplicable.reportLabel }
            : null
        }
      />
      <div
        ref={gridRef}
        onTransitionEnd={onTransitionEnd}
        style={{
          display: "grid",
          gridTemplateRows: expanded ? "1fr" : "0fr",
          transition: reduced
            ? undefined
            : `grid-template-rows ${MOTION.durSlow}ms ${MOTION.easeInOut}`,
        }}
      >
        <div
          ref={clipRef}
          style={{ minHeight: 0, overflow: clip ? "hidden" : "visible" }}
        >
          <div id={panelId} ref={panelRef}>
            <GenericFilterBar
              embedded
              surface={surface}
              showHeader={false}
              filters={drawerFilters}
              filterOptions={filterOptions}
              activeFilters={activeFilters}
              onFilterChange={onFilterChange}
              onClear={onClear}
              onSaveView={onSaveView}
              onUploadSkuList={onUploadSkuList}
              fetchRangeHistogram={fetchRangeHistogram}
            />
            {notApplicable && notApplicableFilters.length > 0 && (
              <p
                data-not-applicable-line=""
                className={`mx-2 mt-2 flex items-start gap-1.5 border-t border-dashed pt-2 text-[11px] ${
                  dark ? "border-white/20 text-white/70" : "border-neutral-300 text-neutral-500"
                }`}
              >
                <MinusCircle size={13} aria-hidden className="mt-0.5 flex-none" />
                <span>
                  <span className={dark ? "font-semibold text-white/85" : "font-semibold text-neutral-700"}>
                    {t("filters.notApplicableLine", { report: notApplicable.reportLabel })}
                  </span>{" "}
                  {notApplicableFilters
                    .map((f) => {
                      const label = resolveFilterLabel(f, t, locale);
                      if (!setNotApplicable.includes(f)) return label;
                      const raw = activeFilters[f.key] ?? "";
                      const value = raw
                        ? formatFilterValue(f, raw, { t, locale, latestDay: filterOptions[f.key]?.[1] })
                        : t("filterbar.all");
                      return `${label} (${value})`;
                    })
                    .join(", ")}
                  . {t("filters.notApplicableKept")}
                </span>
              </p>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
